import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import theme from '../../../theme/theme';
import { Badge } from '../../../components/ui/Badge';
import { BADGE_VARIANTS } from '../../../components/ui/badgeVariants';
import { TruncatedCell } from '../../../components/ui/tableTemplate/TruncatedCell';
import { cellSx } from '../../../components/ui/tableTemplate/cellRenderers';
import type { TableColumn } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import type { LabeledCode } from '../../../lib/labeledCode';
import { COLUMN_WIDTH } from '../../../shared/constants';
import { formatDate, dateFormatForLang } from '../../../lib/date/formatDate';
import { formatPayslipAmount } from '../config/payslipFormat';
import { PAYSLIP_CLOSURE_KEY, type Payslip } from '../payslipTypes';
import {
  PAYSLIP_CLOSURE_VARIANT,
  PAYSLIP_TYPE_VARIANT,
  payslipTypeKey,
  rowTextColor,
} from '../config/payslipPresentation';

// i18n prefixes (namespace-qualified: the header labelKey is resolved by
// TableTemplate's own `useTranslation('common')`, so the `payments:` namespace
// must be explicit). Same convention as `useInstanceColumns` (`instances:columns`).
const I18N_PREFIX = 'payments:payslips.table';
const TYPE_PREFIX = 'payments:payslips.type';
const CLOSURE_STATUS_PREFIX = 'payments:payslips.closureStatus';
const TYPE_OPTIONS_PREFIX = 'payments:payslips.typeOptions';
const CLOSURE_OPTIONS_PREFIX = 'payments:payslips.closureOptions';

/**
 * Stable default for `terrainOptions` — a module-level constant so omitting the
 * prop doesn't create a new array each render (which would defeat the `useMemo`
 * below via a always-changing dependency).
 */
const EMPTY_TERRAIN_OPTIONS: ReadonlyArray<string | LabeledCode> = [];

/** Options controlling which columns are produced. */
export interface UsePayslipColumnsOptions {
  /**
   * Include the Super-Admin-only identifier columns (Contract ID + Payslip ID).
   * Gating by *presence in the column set* (not render-then-hide), per D6/D10.
   */
  showIds: boolean;
  /**
   * Terrain lookup ({@link LabeledCode}[]: `code` = codeterrain, `libelle` = label)
   * used as the option set for the "origin field" list filter. Defaults to `[]`
   * (filter with no options) when the terrain list has not loaded yet.
   */
  terrainOptions?: ReadonlyArray<string | LabeledCode>;
}

/**
 * Read-only payslips table columns (`TableTemplate<Payslip>`). Same shape as
 * `useInstanceColumns` / `useDefaultEmployeeColumns`: a hook that resolves its own
 * i18n (`payments` namespace) and memoizes the column set.
 *
 * Value mappings per the Paiement spec: Type (N → Payslip / O → STC),
 * Closure ('' / P / M → Open / Provisional / Closed), Amount = frozen net to pay.
 * Type/closure colours resolve through the shared {@link BADGE_VARIANTS}; the row
 * text colour shares the type variant's single origin ({@link rowTextColor}).
 */
export function usePayslipColumns({
  showIds,
  terrainOptions = EMPTY_TERRAIN_OPTIONS,
}: UsePayslipColumnsOptions): TableColumn<Payslip>[] {
  const { t, i18n } = useTranslation('payments');
  // Date order follows the active language: fr → dd/MM/yyyy, en → MM/dd/yyyy.
  const dateFormat = dateFormatForLang(i18n.language);

  return useMemo(() => {
    const text = (value: string, color: string): ReactNode => (
      <TruncatedCell text={value} sx={{ ...cellSx, color }} />
    );
    const none = t(`${I18N_PREFIX}.none`);
    const displayDate = (iso: string | null): string =>
      iso ? formatDate(iso, dateFormat) || none : none;
    // Amount formatting shared with the detail view (single origin); grouping
    // follows the selected language.
    const formatAmount = (montant: number | null, currency: string | null) =>
      formatPayslipAmount(montant, currency, none, i18n.language);

    return [
      {
        key: 'mois',
        labelKey: `${I18N_PREFIX}.month`,
        width: COLUMN_WIDTH.text,
        sortable: true,
        // Native month+year picker (YYYYMM) instead of free text — see
        // MonthYearFilterInput. Serializes to `mois[like]=YYYYMM` (exact month).
        filter: { kind: 'monthYear' },
        render: r => text(r.mois, rowTextColor(r)),
      },
      {
        key: 'soldetout_compte',
        labelKey: `${I18N_PREFIX}.type`,
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: TYPE_OPTIONS_PREFIX,
          options: ['N', 'O'],
        },
        render: r => {
          const typeKey = payslipTypeKey(r);
          const colors = BADGE_VARIANTS[PAYSLIP_TYPE_VARIANT[typeKey]];
          return (
            <Badge
              label={t(`${TYPE_PREFIX}.${typeKey}`)}
              color={colors.color}
              bgcolor={colors.bgcolor}
              dot={colors.dot}
              fontWeight={theme.typography.fontWeightSemiBold}
            />
          );
        },
      },
      {
        key: 'date_debut',
        labelKey: `${I18N_PREFIX}.startDate`,
        width: COLUMN_WIDTH.date,
        sortable: true,
        filter: { kind: 'date' },
        render: r => text(displayDate(r.date_debut), rowTextColor(r)),
      },
      {
        key: 'date_fin',
        labelKey: `${I18N_PREFIX}.endDate`,
        width: COLUMN_WIDTH.date,
        sortable: true,
        filter: { kind: 'date' },
        render: r => text(displayDate(r.date_fin), rowTextColor(r)),
      },
      {
        key: 'montant',
        labelKey: `${I18N_PREFIX}.amount`,
        align: 'left',
        width: COLUMN_WIDTH.number,
        sortable: true,
        // Partial match, not exact value: serializes to `montant[like]=` — a free-text
        // substring search (e.g. "123" matches 1234.56), mirroring the backend LIKE filter.
        filter: { kind: 'text' },
        render: r => (
          <TruncatedCell
            text={formatAmount(r.montant, r.devise)}
            sx={{ ...cellSx, color: rowTextColor(r), textAlign: 'left', fontWeight: theme.typography.fontWeightBold }}
          />
        ),
      },
      {
        key: 'paymentdate',
        labelKey: `${I18N_PREFIX}.paymentDate`,
        width: COLUMN_WIDTH.date,
        sortable: true,
        filter: { kind: 'date' },
        render: r => text(displayDate(r.paymentdate), rowTextColor(r)),
      },
      {
        key: 'statut',
        labelKey: `${I18N_PREFIX}.closure`,
        width: COLUMN_WIDTH.text,
        sortable: true,
        // "Open" (statut = NULL in Homère Classic) is filterable via the
        // 'null'/'empty' sentinels; the backend `listWithNull` type expands them
        // to `statut IS NULL (OR = '')`. Same pattern as the employee filters.
        filter: {
          kind: 'list',
          optionLabelPrefix: CLOSURE_OPTIONS_PREFIX,
          options: ['emptyOrNull', 'P', 'M'],
          valueAliases: { emptyOrNull: ['null', 'empty'] },
        },
        render: r => {
          const closure = PAYSLIP_CLOSURE_KEY[r.statut];
          const colors = BADGE_VARIANTS[PAYSLIP_CLOSURE_VARIANT[closure]];
          return (
            <Badge
              label={t(`${CLOSURE_STATUS_PREFIX}.${closure}`)}
              color={colors.color}
              bgcolor={colors.bgcolor}
              dot={colors.dot}
              fontWeight={theme.typography.fontWeightSemiBold}
            />
          );
        },
      },
      {
        key: 'origin_field_bulletin',
        labelKey: `${I18N_PREFIX}.originField`,
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'list', options: terrainOptions },
        render: r =>
          text(r.origin_field_bulletin_libelle ?? r.origin_field_bulletin, rowTextColor(r)),
      },
      ...(showIds
        ? [
            {
              key: 'hc_uuid_key_contrat',
              labelKey: `${I18N_PREFIX}.contractId`,
              // Single UUID — cap the width so the cell truncates and
              // TruncatedCell reveals the full value in a hover tooltip.
              width: COLUMN_WIDTH.text,
              // Identifier column: not filterable/sortable — display-only, the
              // backend exposes neither a filter nor an ORDER BY for the UUID keys.
              render: (r: Payslip) => text(r.hc_uuid_key_contrat, rowTextColor(r)),
            } satisfies TableColumn<Payslip>,
            {
              key: 'hc_uuid_key',
              labelKey: `${I18N_PREFIX}.payslipId`,
              width: COLUMN_WIDTH.text,
              render: (r: Payslip) => text(r.hc_uuid_key, rowTextColor(r)),
            } satisfies TableColumn<Payslip>,
          ]
        : []),
    ];
  }, [t, dateFormat, i18n.language, showIds, terrainOptions]);
}
